import React from 'react';
import { Card, Avatar } from '../../ui-v2';
import { CalendarOff } from 'lucide-react';
import { cargaTecnico } from './progresoResumen';

export default function SeccionTecnicos({ tecnicos }) {
  return (
    <Card className="overflow-hidden p-0 mb-6">
      <div className="px-5 py-4 border-b border-slate-100">
        <h2 className="text-base font-medium text-slate-900">Técnicos</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          {tecnicos.length} {tecnicos.length === 1 ? 'técnico asignado' : 'técnicos asignados'}
        </p>
      </div>

      {tecnicos.length === 0 ? (
        <div className="px-5 py-12 text-center text-sm text-slate-400">
          Esta campaña no tiene técnicos asignados.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-100">
          {tecnicos.map((t) => (
            <CardTecnico key={t.id} tecnico={t} />
          ))}
        </div>
      )}
    </Card>
  );
}

/*
 * 3/11 · B4 — las abiertas se parten en «En curso» (con alguna subtarea marcada, sin cerrar) y «Sin
 * empezar». Antes eran una sola cifra, «Pendientes»; la etiqueta nueva dice «sin empezar» para que
 * nadie lea la cifra nueva como la de antes.
 */
function CardTecnico({ tecnico: t }) {
  const c = cargaTecnico(t.carga);
  const total = c.total;
  const tramo = (n, clase, titulo) => n > 0 && (
    <div className={`h-full ${clase}`} style={{ width: `${(n / total) * 100}%` }} title={titulo} />
  );

  return (
    <div className="bg-white px-5 py-4">
      <div className="flex items-start gap-3">
        <Avatar name={t.nombre_completo} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium text-slate-900 truncate">
              {t.nombre_completo}
            </div>
            {t.tiene_bloqueos && (
              <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5 flex items-center gap-1">
                <CalendarOff className="w-2.5 h-2.5" />
                con bloqueos
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {total} {total === 1 ? 'cita asignada' : 'citas asignadas'}
          </div>
        </div>
      </div>

      {/* Barra de distribución */}
      {total > 0 ? (
        <>
          <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden flex">
            {tramo(c.realizadas, 'bg-primary', `${c.realizadas} realizadas`)}
            {tramo(c.enCurso, 'bg-violet-500', `${c.enCurso} en curso`)}
            {tramo(c.sinEmpezar, 'bg-emerald-400', `${c.sinEmpezar} sin empezar`)}
            {tramo(c.noAsistio, 'bg-amber-400', `${c.noAsistio} no asistió`)}
            {tramo(c.canceladas, 'bg-slate-300', `${c.canceladas} canceladas`)}
          </div>

          <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <Stat color="primary" value={c.realizadas} label="Realizadas" />
            <Stat color="violet" value={c.enCurso} label="En curso" hint="Con subtareas marcadas, sin cerrar" />
            <Stat color="emerald" value={c.sinEmpezar} label="Sin empezar" hint="Con cita y ninguna subtarea marcada" />
            <Stat color="amber" value={c.noAsistio} label="No asistió" />
            <Stat color="slate" value={c.canceladas} label="Canceladas" />
          </div>
        </>
      ) : (
        <p className="text-xs text-slate-400 italic mt-3">Aún sin citas asignadas.</p>
      )}
    </div>
  );
}

function Stat({ color, value, label, hint }) {
  const colorMap = {
    primary: 'text-primary',
    violet: 'text-violet-700',
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    slate: 'text-slate-600',
  };
  return (
    <div title={hint || undefined}>
      <div className={`text-lg font-medium tabular-nums ${colorMap[color]}`}>{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
